import { useMutation, useQueryClient } from '@tanstack/react-query';

import { IGwGPruefung } from '../../Interfaces/IGwGPruefung';
import { customFetcher } from '../useFetch';

/**
 * Die Prüfung wird nicht gelesen, sondern geführt: jeder Schritt gibt den
 * neuen Stand zurück. Deshalb Mutationen statt Abfragen — eine Abfrage, die
 * im Hintergrund neu lädt, würde mitten in der Prüfung die Frage wechseln.
 */
export const useGwGPruefung = () => {
  const queryClient = useQueryClient();

  const invalidiereVorgang = (idLegalTransaction: number) => {
    // Die Pflichtenliste hängt an den Unterlagen des GwG-Ordners; der steckt
    // im fullObject des Vorgangs.
    void queryClient.invalidateQueries({
      queryKey: ['legalTransaction', idLegalTransaction],
    });
  };

  const { mutateAsync: starten, isPending: isPendingStarten } = useMutation({
    mutationFn: (idLegalTransaction: number): Promise<IGwGPruefung> =>
      customFetcher({
        endpoint: `/gwgpruefung/legaltransaction/${idLegalTransaction}`,
        method: 'POST',
        body: {},
      }),
  });

  const { mutateAsync: lesen } = useMutation({
    mutationFn: (idGwGCheck: number): Promise<IGwGPruefung> =>
      customFetcher({ endpoint: `/gwgpruefung/${idGwGCheck}` }),
  });

  const { mutateAsync: antworten, isPending: isPendingAntworten } = useMutation(
    {
      mutationFn: (uploadObject: {
        idGwGCheck: number;
        Answer: string;
        wasPrefilled: boolean;
      }): Promise<IGwGPruefung> =>
        customFetcher({
          endpoint: `/gwgpruefung/${uploadObject.idGwGCheck}/antwort`,
          method: 'PUT',
          body: {
            Answer: uploadObject.Answer,
            wasPrefilled: uploadObject.wasPrefilled,
          },
        }),
    }
  );

  const { mutateAsync: zurueck } = useMutation({
    mutationFn: (uploadObject: {
      idGwGCheck: number;
      NodeKey: string;
    }): Promise<IGwGPruefung> =>
      customFetcher({
        endpoint: `/gwgpruefung/${uploadObject.idGwGCheck}/zurueck`,
        method: 'PUT',
        body: { NodeKey: uploadObject.NodeKey },
      }),
  });

  const { mutateAsync: ergebnisAblegen, isPending: isPendingErgebnis } =
    useMutation({
      mutationFn: (
        idGwGCheck: number
      ): Promise<{ idDocument: number; FileName: string; Ordner: string }> =>
        customFetcher({
          endpoint: `/gwgpruefung/${idGwGCheck}/ergebnis`,
          method: 'POST',
          body: {},
        }),
    });

  return {
    starten,
    ergebnisAblegen,
    isPendingErgebnis,
    lesen,
    antworten,
    zurueck,
    invalidiereVorgang,
    isPendingStarten,
    isPendingAntworten,
  };
};
